import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "../../Services/supabaseClient";
import { useParams } from "react-router-dom";
import { useCompany } from "../../context/CompanyContext";
import { getAccount360 } from "../services/crmAccountsService";
import {
  createActivity,
  completeActivity,
} from "../services/crmActivitiesService";
import ActivityComposer from "../components/ActivityComposer";
import ActivityTimeline from "../components/ActivityTimeline";

const AccountDetailsPage = () => {
  const { accountId } = useParams();
  const { companyId } = useCompany();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyActivity, setBusyActivity] = useState(false);
  const [completingId, setCompletingId] = useState("");
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    if (!companyId || !accountId) return;

    setLoading(true);
    setMessage("");

    try {
      const result = await getAccount360(companyId, accountId);
      setData(result);
    } catch (error) {
      setMessage(error.message || "Failed to load account");
    } finally {
      setLoading(false);
    }
  }, [companyId, accountId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreateActivity = useCallback(
    async (activityPayload) => {
      if (!companyId || !accountId) return;

      setBusyActivity(true);
      setMessage("");

      try {
        const {
          data: { user },
          error: userError,
        } = await supabase.auth.getUser();

        if (userError) throw userError;
        if (!user?.id) throw new Error("User not found");

        await createActivity({
          company_id: companyId,
          owner_id: user.id,
          related_type: "account",
          related_id: accountId,
          activity_type: activityPayload.activity_type,
          subject: activityPayload.subject,
          description: activityPayload.description,
          due_at: activityPayload.due_at,
        });

        await load();
      } catch (error) {
        setMessage(error.message || "Failed to create activity");
      } finally {
        setBusyActivity(false);
      }
    },
    [companyId, accountId, load]
  );

  const handleCompleteActivity = useCallback(
    async (activityId) => {
      if (!companyId || !activityId) return;

      setCompletingId(activityId);
      setMessage("");

      try {
        await completeActivity(activityId, companyId);
        await load();
      } catch (error) {
        setMessage(error.message || "Failed to complete activity");
      } finally {
        setCompletingId("");
      }
    },
    [companyId, load]
  );

  if (loading) return <div style={{ padding: "20px" }}>Loading...</div>;
  if (!data)
    return (
      <div style={{ padding: "20px" }}>{message || "No account data"}</div>
    );

  return (
    <div>
      <div className="crm-record-header">
        <div className="crm-record-header-main">
          <h2 className="crm-record-title">{data.account.name}</h2>
          <p className="crm-record-subtitle">
            Central customer view for contacts, opportunities, quotes,
            collections, payments, and activities.
          </p>

          <div className="crm-record-meta-row">
            <span
              className={`crm-badge status-${String(
                data.account.status || ""
              ).replace(/\s+/g, "_")}`}
            >
              {data.account.status || "N/A"}
            </span>

            <span className="crm-inline-stat">
              Type: {data.account.account_type || "N/A"}
            </span>

            {data.account.email ? (
              <span className="crm-inline-stat">{data.account.email}</span>
            ) : null}
          </div>
        </div>

        <div className="crm-record-aside-actions">
          {/* Optional action buttons can go here later */}
        </div>
      </div>

      {message ? <div className="crm-message">{message}</div> : null}

      <div className="crm-overview-grid">
        <div className="crm-overview-main">
          <h3>Account Overview</h3>

          <div className="crm-overview-fields">
            <div className="crm-overview-field">
              <span className="crm-overview-field-label">Email</span>
              <div className="crm-overview-field-value">
                {data.account.email || "N/A"}
              </div>
            </div>

            <div className="crm-overview-field">
              <span className="crm-overview-field-label">Phone</span>
              <div className="crm-overview-field-value">
                {data.account.phone || "N/A"}
              </div>
            </div>

            <div className="crm-overview-field">
              <span className="crm-overview-field-label">Status</span>
              <div className="crm-overview-field-value">
                {data.account.status || "N/A"}
              </div>
            </div>

            <div className="crm-overview-field">
              <span className="crm-overview-field-label">Type</span>
              <div className="crm-overview-field-value">
                {data.account.account_type || "N/A"}
              </div>
            </div>
          </div>
        </div>

        <div className="crm-overview-side">
          <h3>Quick Highlights</h3>

          <div className="crm-highlight-stack">
            <div className="crm-highlight-box">
              <span>Outstanding Receivables</span>
              <strong>
                ₦
                {Number(
                  data.summary.outstandingReceivables || 0
                ).toLocaleString()}
              </strong>
            </div>

            <div className="crm-highlight-box">
              <span>Open Collections</span>
              <strong>{data.summary.openCollections}</strong>
            </div>
          </div>
        </div>
      </div>

      <div
        className="crm-finance-summary-grid"
        style={{ marginBottom: "18px" }}
      >
        <div className="crm-finance-summary-card">
          <span>Open Opportunities</span>
          <strong>{data.summary.openOpportunities}</strong>
        </div>

        <div className="crm-finance-summary-card">
          <span>Open Quotes</span>
          <strong>{data.summary.openQuotes}</strong>
        </div>

        <div className="crm-finance-summary-card">
          <span>Outstanding Receivables</span>
          <strong>
            ₦{Number(data.summary.outstandingReceivables || 0).toLocaleString()}
          </strong>
        </div>

        <div className="crm-finance-summary-card">
          <span>Total Payments</span>
          <strong>
            ₦{Number(data.summary.totalPayments || 0).toLocaleString()}
          </strong>
        </div>
      </div>

      <div className="crm-detail-section">
        <div className="crm-detail-section-header">
          <h3 className="crm-detail-section-title">Contacts</h3>
        </div>

        {data.contacts.length === 0 ? (
          <div className="crm-empty-state">
            <h4>No contacts yet</h4>
            <p>This account does not have any linked contacts yet.</p>
          </div>
        ) : (
          <div className="crm-entity-list">
            {data.contacts.map((contact) => (
              <div key={contact.id} className="crm-entity-card">
                <div className="crm-entity-card-head">
                  <div className="crm-contact-avatar-row">
                    <div className="crm-contact-avatar">
                      {(contact.full_name || contact.first_name || "U")
                        .slice(0, 1)
                        .toUpperCase()}
                    </div>

                    <div>
                      <h4 className="crm-entity-card-title">
                        {contact.full_name ||
                          `${contact.first_name || ""} ${
                            contact.last_name || ""
                          }`.trim() ||
                          "Unnamed contact"}
                      </h4>
                      <p className="crm-entity-card-subtitle">
                        {contact.job_title || "No title"}
                      </p>
                    </div>
                  </div>

                  {contact.is_primary ? (
                    <span className="crm-badge status-accepted">Primary</span>
                  ) : null}
                </div>

                <div className="crm-entity-card-body">
                  <div className="crm-entity-meta">
                    <strong>Email:</strong> {contact.email || "No email"}
                  </div>
                  <div className="crm-entity-meta">
                    <strong>Phone:</strong> {contact.phone || "No phone"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="crm-detail-section">
        <div className="crm-detail-section-header">
          <h3 className="crm-detail-section-title">Opportunities</h3>
        </div>

        {data.opportunities.length === 0 ? (
          <div className="crm-empty-state">
            <h4>No opportunities yet</h4>
            <p>
              This account does not have any active or historical opportunities
              yet.
            </p>
          </div>
        ) : (
          <div className="crm-entity-list">
            {data.opportunities.map((row) => (
              <div key={row.id} className="crm-entity-card">
                <div className="crm-entity-card-head">
                  <div>
                    <h4 className="crm-entity-card-title">{row.name}</h4>
                    <p className="crm-entity-card-subtitle">
                      Stage: {row.crm_pipeline_stages?.name || "N/A"}
                    </p>
                  </div>

                  <span
                    className={`crm-badge status-${String(
                      row.status || ""
                    ).replace(/\s+/g, "_")}`}
                  >
                    {row.status || "open"}
                  </span>
                </div>

                <div className="crm-entity-card-body">
                  <div className="crm-entity-meta">
                    <strong>Amount:</strong> ₦
                    {Number(row.amount || 0).toLocaleString()}
                  </div>
                  <div className="crm-entity-meta">
                    <strong>Probability:</strong>{" "}
                    {Number(row.probability || 0).toLocaleString()}%
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="crm-detail-section">
        <div className="crm-detail-section-header">
          <h3 className="crm-detail-section-title">Quotes</h3>
        </div>

        {data.quotes.length === 0 ? (
          <div className="crm-empty-state">
            <h4>No quotes yet</h4>
            <p>No quotes have been created for this account yet.</p>
          </div>
        ) : (
          <div className="crm-entity-list">
            {data.quotes.map((row) => (
              <div key={row.id} className="crm-entity-card">
                <div className="crm-entity-card-head">
                  <div>
                    <h4 className="crm-entity-card-title">
                      {row.quote_number || row.id}
                    </h4>
                    <p className="crm-entity-card-subtitle">Quote record</p>
                  </div>

                  <span
                    className={`crm-badge status-${String(
                      row.status || ""
                    ).replace(/\s+/g, "_")}`}
                  >
                    {row.status || "draft"}
                  </span>
                </div>

                <div className="crm-entity-card-body">
                  <div className="crm-entity-meta">
                    <strong>Total:</strong> ₦
                    {Number(row.total || 0).toLocaleString()}
                  </div>
                  <div className="crm-entity-meta">
                    <strong>Created:</strong>{" "}
                    {row.created_at
                      ? new Date(row.created_at).toLocaleDateString()
                      : "N/A"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="crm-detail-section">
        <div className="crm-detail-section-header">
          <h3 className="crm-detail-section-title">Collections</h3>
        </div>

        {data.collections.length === 0 ? (
          <div className="crm-empty-state">
            <h4>No collection cases yet</h4>
            <p>Collection activity for this account will appear here.</p>
          </div>
        ) : (
          <div className="crm-entity-list">
            {data.collections.map((row) => (
              <div key={row.id} className="crm-entity-card">
                <div className="crm-entity-card-head">
                  <div>
                    <h4 className="crm-entity-card-title">{row.status}</h4>
                    <p className="crm-entity-card-subtitle">Collection case</p>
                  </div>

                  <span
                    className={`crm-badge status-${String(
                      row.status || ""
                    ).replace(/\s+/g, "_")}`}
                  >
                    {row.status || "open"}
                  </span>
                </div>

                <div className="crm-entity-card-body">
                  <div className="crm-entity-meta">
                    <strong>Next follow-up:</strong>{" "}
                    {row.next_follow_up_at
                      ? new Date(row.next_follow_up_at).toLocaleString()
                      : "Not set"}
                  </div>
                  <div className="crm-entity-meta">
                    <strong>Note:</strong> {row.note || "N/A"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="crm-detail-section">
        <div className="crm-detail-section-header">
          <h3 className="crm-detail-section-title">Add Activity</h3>
        </div>

        <ActivityComposer onCreate={handleCreateActivity} busy={busyActivity} />
      </div>

      <div className="crm-detail-section">
        <div className="crm-detail-section-header">
          <h3 className="crm-detail-section-title">Activity Timeline</h3>
        </div>

        {data.activities.length === 0 ? (
          <div className="crm-empty-state">
            <h4>No activities yet</h4>
            <p>
              Tasks, calls, meetings, and notes for this account will appear
              here.
            </p>
          </div>
        ) : (
          <ActivityTimeline
            activities={data.activities}
            onComplete={handleCompleteActivity}
            completingId={completingId}
          />
        )}
      </div>
    </div>
  );
};

export default AccountDetailsPage;
